import { trialExtensionModalOpenAtom } from '@/atoms/freeTrialModalAtoms';
import {
  clinicianUpgradeModalOpenAtom,
  upgradeModalFromTrialRecapAtom,
} from '@/atoms/paywallAtoms';
import { clinicianUpgradeModalMessages } from '@/components/modal/clinician-modal/clinician-upgrade-modal.messages';
import { useIsTraineeSpecialty } from '@/components/modal/useIsTraineeSpecialty';
import { useOfflineGatedTooltip } from '@/components/offline-recording/use-offline-gated-tooltip';
import { useIsGamifyTrialArmAllowed } from '@/composition/hosts/growth/post-note-quality/hooks/use-is-gamify-trial-arm-allowed';
import { useMutateActivateClinicianTrial } from '@/hooks/mutation/useMutateActivateClinicianTrial';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import { useQueryGamifyTrialExtension } from '@/hooks/query/useQueryGamifyTrialExtension';
import {
  getNewProductTierProduct,
  getPlanFromPeriod,
  useQueryProducts,
} from '@/hooks/query/useQueryProducts';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { useQueryUserBillingInfo } from '@/hooks/query/useQueryUserBillingInfo';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useFeatureLists } from '@/hooks/useFeatureLists';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import useReverseTrial from '@/hooks/useReverseTrial';
import { getCurrentPlanNameForTrial } from '@/hooks/useUpgradeModalTrialText';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { PaywallSource } from '@/models/analytics/features/payments';
import { trackClinicianUpgradeModalContactSalesClick } from '@/models/analytics/features/trackNinetyDayTrialAnalytics';
import { trackUpgradeToClinicianModalTeamUpgradeRequestSubmit } from '@/models/analytics/features/trackTeamTrialUpgradeRequestAnalytics';
import { isAppleCompliantBuild } from '@/utils/appConfiguration';
import {
  getPlanChangesHelpArticleUrl,
  getTeamContextAnalyticsProps,
  openTeamTrialUpgradeRequestModalAtom,
  pricingPlansModalOpenAtom,
  trialRecapModalMessages,
  useNinetyDayTrialVariant,
  useSubscriptionUpgrade,
  useTrackPaywallViewedOncePerOpen,
} from '@heidi/commerce';
import { type Period } from '@heidi/commerce/contracts';
import { ButtonAction, ScreenName } from '@heidi/runtime/analytics-vocabulary';
import { SubscriptionStatus, SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

export interface UseClinicianUpgradeModalProps {
  launchOpen?: boolean;
  onLaunchClose?: () => void;
}

export function useClinicianUpgradeModal({
  launchOpen,
  onLaunchClose,
}: UseClinicianUpgradeModalProps = {}) {
  // Self-guard: the modal outlives the CTA that opened it, so connectivity is
  // read here too — a session that goes offline while this is open must not
  // leave a live Stripe checkout / trial-activation button behind.
  const { isOfflineGated, gatedTooltip } = useOfflineGatedTooltip();
  const [isAtomOpen, setIsAtomOpen] = useAtom(clinicianUpgradeModalOpenAtom);
  const [isFromTrialRecap, setIsFromTrialRecap] = useAtom(upgradeModalFromTrialRecapAtom);
  const setPricingPlansModalOpen = useSetAtom(pricingPlansModalOpenAtom);
  const { mutate: activateClinicianTrial, isPending: isActivateTrialPending } =
    useMutateActivateClinicianTrial();
  const intl = useIntl();
  const {
    clinicianTrialEnded,
    clinicianTrialDaysLeft,
    isInReverseTrial,
    previousStatusBeforeClinicianTrial,
    isClinicianTrialUser,
    isFakeClinicianTrial,
  } = useReverseTrial();
  const setTrialExtensionModalOpen = useSetAtom(trialExtensionModalOpenAtom);
  const openTeamTrialUpgradeRequestModal = useSetAtom(
    openTeamTrialUpgradeRequestModalAtom
  );
  const isOpen = isAtomOpen || (launchOpen ?? false);
  const showTraineeVerificationNudge = useIsTraineeSpecialty(isOpen);
  const { userTier } = useQueryUserTier();
  const {
    hasTeam,
    isTeamAdmin,
    isTeamStripeManaged,
    isTeamDataReady,
    isJoinedTeamsFetched,
  } = useCombinedQueriesTeamGeneralInfo();
  const { data: userData } = useQueryUser((data) => ({
    free_trial_history_tiers: data?.free_trial_history_tiers,
  }));

  const handleClose = useCallback(() => {
    setIsAtomOpen(false);
    setIsFromTrialRecap(false);
    onLaunchClose?.();
  }, [setIsAtomOpen, setIsFromTrialRecap, onLaunchClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      } else {
        setIsAtomOpen(true);
      }
    },
    [handleClose, setIsAtomOpen]
  );

  const { data: products, catalogueVersion } = useQueryProducts({
    heidi2Catalogue: true,
  });
  const clinicianProduct = getNewProductTierProduct(
    SubscriptionTier.CLINICIAN_PRO,
    products
  );
  const { data: billingInfo } = useQueryUserBillingInfo();
  const newProductTier = billingInfo?.new_product_tier;

  const hasClinicianTierInFreeTrialHistory = useMemo(
    () =>
      userData?.free_trial_history_tiers?.some(
        (tier) =>
          tier === SubscriptionTier.CLINICIAN_PRO ||
          tier === SubscriptionTier.CLINICIAN_PRO_PLUS
      ) ?? false,
    [userData?.free_trial_history_tiers]
  );

  const trialCTAMode =
    newProductTier === SubscriptionTier.FREE && !hasClinicianTierInFreeTrialHistory;

  const {
    showNinetyDayTrialVariant: rawShowNinetyDayTrialVariant,
    isNinetyDayTrialEnded,
    isUpgradeDisabledForTeamMember,
    shouldDisableButtonForNonStripeManagedTeam,
    showNonAdminRequestBanner: rawShowNonAdminRequestBanner,
    showContactSalesInsteadOfUpgrade: rawShowContactSalesInsteadOfUpgrade,
    planDisplayName: clinicianPlanDisplayName,
    handleContactSalesClick,
    hasCallbackRequested,
  } = useNinetyDayTrialVariant({
    isOpen,
    excludeFromVariant: trialCTAMode,
    trackContactSalesClick: trackClinicianUpgradeModalContactSalesClick,
    planNameMessage: trialRecapModalMessages.clinicianPlanName,
    onClose: handleClose,
  });

  useTrackPaywallViewedOncePerOpen({
    isOpen,
    isTeamsFetched: isJoinedTeamsFetched,
    isTeamDataReady,
    hasTeam,
    onTrack: () => {
      track({
        options: { amplitude: false },
        event_name: TrackingEventName.PaywallViewed,
        props: {
          source: PaywallSource.ClinicianUpgradeModal,
          target_plan: SubscriptionTier.CLINICIAN_PRO,
          days_left_on_trial: clinicianTrialDaysLeft,
          is_90_day_trial_user: isFakeClinicianTrial,
          is_90_day_trial_ended: isNinetyDayTrialEnded,
          ...getTeamContextAnalyticsProps({
            hasTeam,
            isTeamAdmin,
            isTeamStripeManaged,
          }),
        },
      });
    },
  });

  // When opened from the post-90-day trial-ended recap modal we re-use the
  // 90-day variant layout (grouped pricing card, UpgradeTrialFeatureList,
  // UpgradeTermsFooter, "Keep Clinician" CTA) even though the trial has
  // technically ended. The modal component additionally swaps the title and
  // hides the testimonial right panel for this variant.
  const showNinetyDayTrialVariant = rawShowNinetyDayTrialVariant || isFromTrialRecap;
  const showNonAdminRequestBanner =
    rawShowNonAdminRequestBanner || (isFromTrialRecap && isUpgradeDisabledForTeamMember);
  const showContactSalesInsteadOfUpgrade =
    rawShowContactSalesInsteadOfUpgrade ||
    (isFromTrialRecap && shouldDisableButtonForNonStripeManagedTeam);

  const { handleUpgrade, isPending } = useSubscriptionUpgrade({
    onCheckoutComplete: handleClose,
  });
  const handleUpgradeClick = useCallback(
    (period: Period) => {
      if (isOfflineGated) {
        return;
      }

      track({
        event_name: TrackingEventName.ButtonClicked,
        props: {
          screen_name: ScreenName.UpgradeToClinicianModal,
          action: ButtonAction.ClinicianUpgradeModalUpgrade,
          label: intl.formatMessage(clinicianUpgradeModalMessages.upgradeNow),
          target_plan: SubscriptionTier.CLINICIAN_PRO,
          period,
          trial_days_remaining: clinicianTrialDaysLeft,
          ...(userTier !== undefined && { current_plan: userTier }),
          is_90_day_trial_user: isFakeClinicianTrial,
          is_90_day_trial_ended: isNinetyDayTrialEnded,
        },
      });
      void handleUpgrade(
        period,
        SubscriptionTier.CLINICIAN_PRO,
        catalogueVersion === 'v3'
          ? (getPlanFromPeriod(period, clinicianProduct)?.id ?? null)
          : undefined
      );
    },
    [
      handleUpgrade,
      catalogueVersion,
      clinicianProduct,
      clinicianTrialDaysLeft,
      userTier,
      isFakeClinicianTrial,
      isNinetyDayTrialEnded,
      intl,
      isOfflineGated,
    ]
  );

  const currentPlanName = getCurrentPlanNameForTrial(
    isInReverseTrial,
    previousStatusBeforeClinicianTrial
  );

  const showExpiredTitle =
    clinicianTrialEnded || newProductTier !== SubscriptionTier.CLINICIAN_PRO;

  const titleText = useMemo(() => {
    if (showExpiredTitle) {
      return intl.formatMessage(clinicianUpgradeModalMessages.titleLevelUpCare);
    }
    if (clinicianTrialDaysLeft > 3) {
      return intl.formatMessage(clinicianUpgradeModalMessages.titleFreeTrialClinician);
    }
    if (clinicianTrialDaysLeft <= 0) {
      return intl.formatMessage(clinicianUpgradeModalMessages.titleLastDayClinician);
    }
    return intl.formatMessage(
      clinicianUpgradeModalMessages.titleDaysLeftToKeepClinician,
      {
        days: clinicianTrialDaysLeft,
      }
    );
  }, [intl, showExpiredTitle, clinicianTrialDaysLeft]);

  const loseAccessText = useMemo(() => {
    if (showExpiredTitle) {
      return undefined;
    }
    return clinicianTrialDaysLeft <= 0
      ? intl.formatMessage(clinicianUpgradeModalMessages.loseAccessToday)
      : intl.formatMessage(clinicianUpgradeModalMessages.loseAccessDays, {
          days: clinicianTrialDaysLeft,
        });
  }, [intl, showExpiredTitle, clinicianTrialDaysLeft]);

  const hookFeaturesTitle = useMemo(() => {
    if (showExpiredTitle) {
      return undefined;
    }
    if (clinicianTrialDaysLeft > 3) {
      if (currentPlanName !== undefined && currentPlanName !== null) {
        return intl.formatMessage(clinicianUpgradeModalMessages.everythingInPlanPlus, {
          planName: currentPlanName,
        });
      }
      return intl.formatMessage(
        clinicianUpgradeModalMessages.everythingInCurrentPlanPlus
      );
    }
    return loseAccessText ?? undefined;
  }, [currentPlanName, intl, loseAccessText, showExpiredTitle, clinicianTrialDaysLeft]);

  const isShortTrial = !showExpiredTitle && clinicianTrialDaysLeft <= 3;

  const featuresTitle = useMemo(() => {
    if (trialCTAMode) return undefined;
    if (isClinicianTrialUser) return hookFeaturesTitle;
    if (userTier === SubscriptionTier.PRO) {
      return intl.formatMessage(clinicianUpgradeModalMessages.everythingInScribePlus);
    }
    if (userTier === SubscriptionTier.EVIDENCE) {
      return intl.formatMessage(clinicianUpgradeModalMessages.everythingInEvidencePlus);
    }
    if (userTier === SubscriptionTier.FREE) {
      return intl.formatMessage(clinicianUpgradeModalMessages.everythingInFree);
    }
    return undefined;
  }, [trialCTAMode, isClinicianTrialUser, userTier, hookFeaturesTitle, intl]);

  const handleStartTrial = useCallback(() => {
    if (isOfflineGated) {
      return;
    }
    activateClinicianTrial(undefined, { onSuccess: handleClose });
  }, [activateClinicianTrial, handleClose, isOfflineGated]);

  const handleExtendTrial = useCallback(() => {
    handleClose();
    setTrialExtensionModalOpen(true);
  }, [handleClose, setTrialExtensionModalOpen]);

  const handleTraineeVerificationClick = useCallback(() => {
    track({
      event_name: TrackingEventName.ButtonClicked,
      props: {
        screen_name: ScreenName.UpgradeToClinicianModal,
        action: ButtonAction.StudentVerificationCtaClicked,
        label: intl.formatMessage(
          clinicianUpgradeModalMessages.traineeVerificationNudge,
          {
            traineeLink: (chunks) => chunks.join(''),
          }
        ),
        trial_days_remaining: clinicianTrialDaysLeft,
      },
    });
  }, [clinicianTrialDaysLeft, intl]);

  const { clinicianFeatures, clinicianShortFeatures, evidenceFeaturesFromPro } =
    useFeatureLists();
  const { isUKOrEU } = useIsUKOrEU();

  const wasPreviouslyPro =
    previousStatusBeforeClinicianTrial === SubscriptionStatus.PRO ||
    userTier === SubscriptionTier.PRO;

  const subtitle = useMemo(() => {
    if (trialCTAMode) {
      return intl.formatMessage(clinicianUpgradeModalMessages.trialCTASubtitle);
    }
    if (isClinicianTrialUser) {
      return intl.formatMessage(clinicianUpgradeModalMessages.subtitleClinician);
    }
    if (userTier === SubscriptionTier.PRO) {
      return intl.formatMessage(
        clinicianUpgradeModalMessages.subtitleClinicianForScribePlus
      );
    }
    return intl.formatMessage(clinicianUpgradeModalMessages.subtitleClinicianForNonTrial);
  }, [trialCTAMode, userTier, isClinicianTrialUser, intl]);

  const modalTitleText = useMemo(() => {
    if (trialCTAMode) {
      return intl.formatMessage(clinicianUpgradeModalMessages.titleLevelUpCare);
    }
    return titleText;
  }, [trialCTAMode, intl, titleText]);

  const gamifyTrialExtensionQueryEnabled =
    !trialCTAMode && isInReverseTrial && !clinicianTrialEnded;
  const { data: gamifyTrialExtension } = useQueryGamifyTrialExtension(
    gamifyTrialExtensionQueryEnabled
  );
  // GRO-3284: suppress (Arm B) / delay until the sidebar is done (Arm C).
  const isGamifyArmAllowed = useIsGamifyTrialArmAllowed();

  const showExtendTrialButton =
    !trialCTAMode &&
    !isUpgradeDisabledForTeamMember &&
    !shouldDisableButtonForNonStripeManagedTeam &&
    isInReverseTrial &&
    !clinicianTrialEnded &&
    !gamifyTrialExtension?.trial_extended &&
    isGamifyArmAllowed;

  const handleViewAllPlans = useCallback(() => {
    handleClose();
    setPricingPlansModalOpen(true);
  }, [handleClose, setPricingPlansModalOpen]);

  const handleOpenTeamTrialUpgradeRequestModal = useCallback(() => {
    handleClose();
    openTeamTrialUpgradeRequestModal({
      plan: SubscriptionTier.CLINICIAN_PRO,
      trackRequestSubmitted: trackUpgradeToClinicianModalTeamUpgradeRequestSubmit,
      onRequestSent: () => {
        handleClose();
      },
      onUserDismiss: () => {
        handleOpenChange(true);
      },
    });
  }, [handleClose, handleOpenChange, openTeamTrialUpgradeRequestModal]);

  const planChangesArticleUrl = useMemo(
    () => getPlanChangesHelpArticleUrl(intl.locale),
    [intl.locale]
  );

  const modalFeatures = useMemo(() => {
    const features = wasPreviouslyPro
      ? evidenceFeaturesFromPro
      : isShortTrial
        ? clinicianShortFeatures
        : clinicianFeatures;

    if (!isUKOrEU) return features;

    return features.filter((feature) => !feature.notAvailableInRestrictedMarkets);
  }, [
    clinicianFeatures,
    clinicianShortFeatures,
    evidenceFeaturesFromPro,
    isShortTrial,
    isUKOrEU,
    wasPreviouslyPro,
  ]);

  return {
    isOpen,
    handleOpenChange,
    handleUpgradeClick,
    handleStartTrial,
    handleExtendTrial,
    handleTraineeVerificationClick,
    clinicianTrialDaysLeft,
    trialCTAMode,
    // Offline wins over every other branch, including the non-admin banner that
    // otherwise forces the button back on.
    isUpgradeDisabled: isOfflineGated
      ? true
      : showNonAdminRequestBanner
        ? false
        : trialCTAMode
          ? isUpgradeDisabledForTeamMember
          : isUpgradeDisabledForTeamMember ||
            (shouldDisableButtonForNonStripeManagedTeam &&
              !showContactSalesInsteadOfUpgrade),
    upgradeDisabledTooltip: gatedTooltip(
      isUpgradeDisabledForTeamMember
        ? intl.formatMessage(clinicianUpgradeModalMessages.contactAdminToUpgrade)
        : shouldDisableButtonForNonStripeManagedTeam
          ? intl.formatMessage(clinicianUpgradeModalMessages.contactSalesToUpgrade)
          : intl.formatMessage(clinicianUpgradeModalMessages.contactAdminToUpgrade)
    ),
    modalTitleText,
    subtitle,
    modalFeaturesTitle: featuresTitle,
    features: modalFeatures,
    clinicianProduct,
    isActivateTrialPending,
    isPending,
    showExtendTrialButton,
    handleViewAllPlans,
    handleClose,
    showNinetyDayTrialVariant,
    showNonAdminRequestBanner,
    showContactSalesInsteadOfUpgrade,
    handleContactSalesClick,
    hasCallbackRequested,
    clinicianPlanDisplayName,
    isFromTrialRecap,
    isNinetyDayTrialEnded,
    planChangesArticleUrl,
    showTraineeVerificationNudge,
    handleOpenTeamTrialUpgradeRequestModal,
    isOfflineGated,
    isAppleCompliant: isAppleCompliantBuild(),
  };
}
